import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="22946" className="hidden lg:flex gap-8 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-bold uppercase tracking-widest text-foreground/90 hover:text-primary transition-colors font-sans relative group">
                {item.label}
                <span className="absolute -bottom-1 left-1/2 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full group-hover:left-0" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8 w-full items-center">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted/20 rounded animate-pulse" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center justify-center gap-8 p-8 w-full">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-bold uppercase tracking-widest text-accent-foreground hover:brightness-125 transition-all font-sans" onClick={onClose}>
          {item.label}
        </Link>)}
      <Button className="mt-4 w-full max-w-xs font-extrabold tracking-widest rounded-full" size="lg" variant="primary" asChild>
        <Link to="/ContactPage" onClick={onClose}>
          GET A QUOTE
        </Link>
      </Button>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <>
      <header id="navigation-header" className={`sticky top-0 z-50 w-full transition-all duration-500 dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-white/10 shadow-xl" : "bg-background border-b border-transparent"}`}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {}
          <div className="flex-shrink-0 z-50">
            <Link to="/HomePage" className="block" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://coastal-prestige.websiteshop.au/wp-content/uploads/2026/03/Coastal-Prestige-Final-1-scaled.png" alt="Coastal Prestige Mowing & Maintenance" className="w-auto object-contain brightness-110 !h-[75px] ml-[120px] lg:ml-0" />
            </Link>
          </div>

          {}
          <div className="hidden flex-1 justify-center ![margin:0px_0px_0px_-40px] lg:flex">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex items-center flex-shrink-0">
            <Button variant="primary" className="font-extrabold tracking-widest uppercase rounded-full px-8 transition-transform hover:scale-105 active:scale-95" asChild>
              
            </Button>
          </div>

          {}
          <div className="lg:hidden flex items-center relative z-[60]">
            <Button variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-transparent shadow-[0_0_20px_rgba(34,197,94,0.15)]" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </header>

      {}
      <div className={`fixed top-20 left-0 right-0 bottom-0 z-[100] bg-[#0b0b0b] backdrop-blur-2xl transition-all duration-500 lg:hidden flex flex-col ${isMobileMenuOpen ? "translate-x-0 opacity-100 visible" : "translate-x-full opacity-0 invisible"}`}>
        {}
        <div className="flex items-center justify-end p-4 h-20 border-b border-white/10">
          <Button variant="ghost" size="icon" className="text-accent-foreground hover:brightness-125 hover:bg-transparent" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
            <X className="h-6 w-6" />
          </Button>
        </div>

        {}
        <div className="flex-1 flex items-center justify-center overflow-y-auto w-full">
          <MenuProvider menu_id="16">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </>;
}